import { api } from '@/api';
import { LoginResult, Member, MfaLoginResult, SignupInput } from '@vivport/backend/types';

import { AxiosError } from 'axios';
import {
    clearTokens,
    getAccessToken,
    getRefreshToken,
    setTokens,
} from './token-utils';

/**
 * Auth Utilities
 * Core authentication functions adapted for Vivport's Account + Members structure
 */

export interface AuthData extends LoginResult {
  selectedMember: Member;
}

/**
 * Check authentication status by verifying tokens and fetching user data
 */
export const checkAuth = async (): Promise<AuthData | null> => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return null;
  }

  if (accessToken && refreshToken) {
    try {
      const userInfo = await api.auth.getUser();
      const selectedMember = resolveMember(userInfo.members);
      
      if (!selectedMember) {
        throw new Error('No valid member found');
      }

      return {
        account: userInfo.account,
        members: userInfo.members,
        selectedMember,
        accessToken,
        refreshToken
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Try to refresh token
          try {
            const refreshResult = await api.auth.refreshToken({ refreshToken });
            const newAccessToken = refreshResult.accessToken;
            const newRefreshToken = refreshResult.refreshToken;
            
            setTokens(newAccessToken, newRefreshToken);
            
            // Get user data with new token
            const userInfo = await api.auth.getUser();
            const selectedMember = resolveMember(userInfo.members);
            
            if (!selectedMember) {
              throw new Error('No valid member found');
            }

            return {
              ...userInfo,
              selectedMember,
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
            };
          } catch (refreshError) {
            // Refresh failed. Avoid clearing tokens if another flow (e.g. fresh login)
            // already rotated them while this stale checkAuth request was in flight.
            const latestRefreshToken = getRefreshToken();
            if (latestRefreshToken === refreshToken) {
              clearTokens();
              localStorage.removeItem('memberId');
            }
            return null;
          }
        }
      }
      throw error;
    }
  }

  return null;
};

/**
 * Login with email and password
 */
export const login = async ({ 
  email, 
  password 
}: { 
  email: string; 
  password: string 
}): Promise<AuthData | MfaLoginResult> => {
  // Validate inputs before sending
  if (!email || !password) {
    throw new Error('Please provide both email and password.');
  }
  
  try {
    const result = await api.auth.login({ email, password });
    
    // Check if MFA is required
    if ('mfaRequired' in result) {
      return result;
    }

    return completeLogin(result);
  } catch (error) {
    if (error instanceof AxiosError) {
      // Use backend error message if available
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }
      if (error.response?.status === 400) {
        const backendMessage = error.response?.data?.message || error.response?.data?.error;
        throw new Error(backendMessage || 'Please provide both email and password.');
      }
      if (error.response?.status === 500) {
        throw new Error('Server error. Please try again later.');
      }
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
    }
    
    // Re-throw if it's already an Error
    if (error instanceof Error) {
      throw error;
    }
    
    throw new Error('Login failed. Please try again.');
  }
};

export type SignupCheckoutOutcome =
  | { kind: 'checkout'; url: string }
  | { kind: 'complete'; auth: AuthData };

export const LANDING_FILTER_STORAGE_KEY = 'vivport:landingFilterId';

/**
 * The single login completion path: every flow that ends with a LoginResult
 * (password, signup, email MFA, passkey MFA) goes through here.
 */
export const completeLogin = (result: LoginResult): AuthData => {
  setTokens(result.accessToken, result.refreshToken);
  const landingMember = result.members.find((m) => m.isLoginLanding);
  const selectedMember = landingMember ?? resolveMember(result.members);
  localStorage.setItem('memberId', selectedMember.id.toString());
  // One-shot landing-view stash: written on login completion AND member switch
  // (the landed member's own view), consumed exactly once by LayoutExplore.
  // A missing/invalid reference is ignored there and the default tab applies.
  if (selectedMember.landingFilterId !== null) {
    sessionStorage.setItem(LANDING_FILTER_STORAGE_KEY, String(selectedMember.landingFilterId));
  }
  return { ...result, selectedMember };
};

const toSignupError = (error: unknown): Error => {
  if (error instanceof AxiosError) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    if (error.response?.status === 409) {
      return new Error('An account with this email or account name already exists.');
    }
    if (error.response?.status === 500) {
      return new Error('Server error. Please try again later.');
    }
    if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
      return new Error('Network error. Please check your internet connection and try again.');
    }
  }
  if (error instanceof Error) {
    return error;
  }
  return new Error('Signup failed. Please try again.');
};

/**
 * Start signup: redirects to Stripe checkout, or logs straight in when the
 * email has a 100% discount
 */
export const signupCheckout = async (input: SignupInput): Promise<SignupCheckoutOutcome> => {
  try {
    const result = await api.auth.signupCheckout(input);
    return result.kind === 'complete' ? { kind: 'complete', auth: completeLogin(result.login) } : result;
  } catch (error) {
    throw toSignupError(error);
  }
};

/**
 * Complete signup after returning from Stripe checkout and log in
 */
export const signupComplete = async (sessionId: string): Promise<AuthData> => {
  try {
    const result = await api.auth.signupComplete({ sessionId });
    return completeLogin(result);
  } catch (error) {
    throw toSignupError(error);
  }
};

/**
 * Refresh access token using refresh token
 */
export const refreshToken = async (): Promise<{ accessToken: string; refreshToken: string }> => {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    throw new Error('No refresh token found. Please log in again.');
  }
  
  try {
    const result = await api.auth.refreshToken({ refreshToken: refreshTokenValue });
    const { accessToken, refreshToken: newRefreshToken } = result;
    
    setTokens(accessToken, newRefreshToken);
    
    return {
      accessToken,
      refreshToken: newRefreshToken
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        clearTokens();
        throw new Error('Your session has expired. Please log in again.');
      }
    }
    throw new Error('Failed to refresh token. Please log in again.');
  }
};

/**
 * Logout user
 */
export const logout = async (): Promise<void> => {
  const refreshTokenValue = getRefreshToken();
  
  try {
    if (refreshTokenValue) {
      await api.auth.logout({ refreshToken: refreshTokenValue });
    }
  } catch (error) {
    // Log the error but don't throw - logout should succeed even if API fails
    console.error('Logout API failed', error);
  } finally {
    // Always clear local state
    clearTokens();
    localStorage.removeItem('memberId');
  }
};

/**
 * Resolve which member to use from the members array
 * Logic adapted from authStore.applyMember()
 */
export const resolveMember = (members: [Member, ...Member[]]): Member => {
  let chosenMember: Member | undefined;

  // Try localStorage
  const storedMemberId = localStorage.getItem('memberId');
  if (storedMemberId) {
    chosenMember = members.find(m => m.id.toString() === storedMemberId);
  }
  
  // If still not found, prefer non-personal members over personal ones
  if (!chosenMember) {
    const nonPersonalMembers = members.filter(m => !m.isPersonal);
    if (nonPersonalMembers.length > 0) {
      chosenMember = nonPersonalMembers.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];
    } else {
      // Fall back to personal member
      chosenMember = members.find(m => m.isPersonal);
    }
  }

  // If still not found, just pick the first member
  if (!chosenMember) {
    chosenMember = members[0];
  }

  return chosenMember || null;
};

/**
 * Check if member is hub admin
 */
export const isHubAdmin = (member: Member): boolean => {
  return !!member.isAdmin;
};

